import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import AudioListen from "@/components/AudioListen";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RITUAL_SCRIPT = `Before you enter your shift, pause. Feel your feet on the ground. Breathe in through your nose for four seconds. Hold that breath for four. Release it slowly through your mouth for six. Again. In for four. Hold for four. Out for six. Whatever weight you carried into this room does not have to walk in with you. You are allowed to set it down for the next eight hours. When you are ready, open your hands, unclench your jaw, and step forward. Through adversity comes change. Go do the work.`;

export default function PreShiftRitual() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(""); // "" | "new" | "already"

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/newsletter`, { email });
      const already = data.status === "already_subscribed";
      setSubscribed(already ? "already" : "new");
      toast.success(already ? "You're already on the list." : "Sent. Check your inbox for the ritual.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Try again.");
    } finally { setLoading(false); }
  };

  return (
    <section className="relative bg-[#FFFFFF] py-24 md:py-32 border-y border-[#E7DFD5] overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-y-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.65, 0, 0.35, 1] }}
            className="col-span-12 md:col-span-7"
          >
            <div className="section-tag mb-6">§ Free · 90 seconds</div>
            <h2 className="font-display text-5xl md:text-6xl leading-[0.95] tracking-tight mb-6">
              The <span className="italic text-[#B45309]">Pre-Shift</span> Ritual.
            </h2>
            <p className="text-[#78716C] text-lg leading-relaxed max-w-xl mb-8">
              A ninety-second guided reset — narrated. Ground before you enter. Listen with headphones, ideally in the parking lot before you clock in.
            </p>
            <AudioListen text={RITUAL_SCRIPT} label="Play the ritual" variant="block" testid="ritual-play" />

            <div className="mt-10 pt-8 border-t border-[#E7DFD5] max-w-xl">
              <div className="section-tag mb-3">§ Get it in your inbox</div>
              <p className="text-[#78716C] text-sm leading-relaxed mb-4">
                Written script + Chapter Zero of the manual, sent once. No spam. Unsubscribe any time.
              </p>
              {subscribed ? (
                <div data-testid="ritual-newsletter-success" className="text-sm text-[#B45309] font-mono tracking-[0.15em] uppercase">
                  {subscribed === "already" ? "✓ You're already on the list." : "✓ Sent. Check your inbox."}
                </div>
              ) : (
                <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3" data-testid="ritual-newsletter-form">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    data-testid="ritual-newsletter-email"
                    className="flex-1 bg-[#FFFFFF] border border-[#E7DFD5] px-4 py-3.5 text-sm text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:border-[#F59E0B] transition-colors duration-300"
                  />
                  <button disabled={loading} data-testid="ritual-newsletter-submit" className="btn-outline text-sm justify-center">
                    {loading ? "Sending…" : "Send me the ritual"}
                    <ArrowUpRight size={16} />
                  </button>
                </form>
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1, ease: [0.65, 0, 0.35, 1] }}
            className="col-span-12 md:col-span-4 md:col-start-9 flex items-center justify-center"
          >
            {/* pulsing waveform emblem */}
            <div className="relative w-56 h-56">
              <div className="absolute inset-0 rounded-full border border-[#F59E0B]/30 animate-ping" />
              <div className="absolute inset-4 rounded-full border border-[#F59E0B]/20" />
              <div className="absolute inset-10 rounded-full border border-[#F59E0B]/10" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="font-display text-6xl text-[#B45309]">◉</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
